import { createContext, useContext } from 'react';

import type { DeepPartial, PlayerSettings } from '../types';

export const defaultPlayerSettings: PlayerSettings = {
  environment: {
    useBackgroundGradient: true,
    backdropHexColor: '#42aad7',
    floor: 'shadow',
    showGrid: false,
    gridCellSize: 1,
    gridColor: '#7A7A7A',
  },
  camera: {
    follow: false,
    aspectRatio: -1,
  },
  scene: {
    showActorNameTags: false,
    dimInactiveActorNameTags: true,
  },
  playback: {
    frameRate: 24,
    frameStepSize: 1,
    playbackSpeed: 1,
    autoplay: true,
    disabled: false,
    loop: true,
  },
  character: {
    skinHex: undefined,
    faceExpression: undefined,
    showSkeleton: false,
  },
};

export type PlayerSettingsContextValue = {
  settings: PlayerSettings;
  setSettings: (next: DeepPartial<PlayerSettings>) => void;
};

export const PlayerSettingsContext =
  createContext<PlayerSettingsContextValue | null>(null);

export function mergePlayerSettings(
  base: PlayerSettings,
  patch?: DeepPartial<PlayerSettings>,
): PlayerSettings {
  if (!patch) return base;

  return {
    environment: {
      ...base.environment,
      ...patch.environment,
    },
    camera: {
      ...base.camera,
      ...patch.camera,
    },
    scene: {
      ...base.scene,
      ...patch.scene,
    },
    playback: {
      ...base.playback,
      ...patch.playback,
    },
    character: {
      ...base.character,
      ...patch.character,
    },
  };
}

export function usePlayerSettingsContext() {
  const context = useContext(PlayerSettingsContext);
  if (!context) {
    throw new Error(
      'usePlayerSettingsContext must be used inside <Player.Root>.',
    );
  }
  return context;
}
